import { useCallback, useEffect, useMemo, useState } from 'react';

import { GetAssetLocationsRequestDTO, MODULE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import DownloadIcon from '@/components/icons/download';
import UndoIcon from '@/components/icons/undo';
import CreateLocationLogDrawer from '@/components/location-logs/create-location-log-drawer';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { buildFilterFields } from '@/queries/column-filter';
import { getLocationLogsInfiniteQueryOptions, locationLogKeys } from '@/queries/locations';
import { useModulesStore } from '@/stores/modules.store';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';

import { LocationLogsTable, NoLocationLogsCreatedDisplay, NoLocationLogsFoundFromFiltersDisplay } from './table';
import { columns, FILTER_FIELD_MAP, GROUP_OPTIONS } from './table-meta';

function AssetLocationLogsPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const toast = useToast();

  const { setModuleFilters } = useModulesStore();

  const {
    config: { columnOrder, columnVisibility, grouping, sorting, filters },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.AssetLocationLogs, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions: GROUP_OPTIONS,
    columns,
  });

  const [createLocationLogDrawerOpen, setCreateLocationLogDrawerOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});

  const {
    data: locationLogsData,
    fetchNextPage: fetchNextLocationLogsPage,
    hasNextPage: hasNextLocationLogsPage,
    isLoading: isLoadingLocationLogs,
    isFetching: isFetchingLocationLogs,
    isFetchingNextPage: isFetchingNextLocationLogsPage,
    error: locationLogsError,
  } = useInfiniteQuery(
    getLocationLogsInfiniteQueryOptions({
      filters: buildFilterFields(filters, FILTER_FIELD_MAP, {
        extraFields: { assetId: [id as string] },
      }),
      groupBy: grouping.length
        ? {
            field: grouping[0] as NonNullable<GetAssetLocationsRequestDTO['groupBy']>['field'],
            direction: (sorting.direction || 'asc') as 'asc' | 'desc',
          }
        : undefined,
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetAssetLocationsRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : {
            field: 'createdAt',
            direction: 'desc',
          },
      enabled: !isLoadingPageConfig,
    }),
  );

  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextLocationLogsPage,
    hasNextPage: hasNextLocationLogsPage,
    isFetching: isFetchingLocationLogs,
    isError: !!locationLogsError,
    root: scrollRoot,
  });

  useEffect(() => {
    if (locationLogsError) {
      toast.error(locationLogsError.message);
    }
  }, [toast, locationLogsError]);

  useResetRowSelectionOnChange(setSelectedRows, [filters, sorting.key, sorting.direction, grouping]);

  const flattenedData = useMemo(() => locationLogsData?.pages.flatMap((page) => page.data) ?? [], [locationLogsData]);

  const selectedLocationLogs = useMemo(() => {
    const ids = Object.keys(selectedRows);
    return ids.filter((id) => !isNaN(parseInt(id))).map((id) => flattenedData[parseInt(id)]);
  }, [selectedRows, flattenedData]);

  const { canExportCsv } = usePermissions();

  const handleDownload = useCallback(() => {
    const activeColumnIds = columnOrder.filter((column) => column.id !== 'select' && column.active).map((column) => column.id);
    openExportTab({
      kind: 'csv',
      request: {
        queryType: 'location-logs',
        filename: buildCsvExportFilename('location-logs'),
        filters: { fields: { id: selectedLocationLogs.map((log) => log.id) } },
        groupBy: grouping.length
          ? { field: grouping[0] as NonNullable<GetAssetLocationsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
          : undefined,
        orderBy: sorting.key
          ? { field: sorting.key as NonNullable<GetAssetLocationsRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
          : undefined,
        columns: activeColumnIds,
      },
    });
  }, [selectedLocationLogs, columnOrder, grouping, sorting]);

  const isLoading = isLoadingPageConfig || isLoadingLocationLogs;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            <div className="flex h-full items-center gap-x-2 px-2">
              {filters && filters.length > 0 && (
                <TablePageHeaderActionButton
                  icon={<UndoIcon />}
                  label="Remove all Filters"
                  kind="secondary"
                  onClick={() => setModuleFilters(MODULE.AssetLocationLogs, [])}
                />
              )}
              {selectedLocationLogs.length > 0 && (
                <>
                  <TablePageSelectedCountIndicator selectedCount={selectedLocationLogs.length} totalCount={locationLogsData?.pages[0]?.totalCount ?? 0} />
                  {canExportCsv && <TablePageHeaderActionButton icon={<DownloadIcon />} label="Download CSV" onClick={handleDownload} />}
                </>
              )}
            </div>

            <div className="mr-1 flex h-full items-center">
              <TablePageTableGroupDropdown
                columns={GROUP_OPTIONS}
                value={grouping.length ? grouping[0] : ''}
                onChange={(value) => updatePageConfig({ table: { grouping: value ? [value] : [] } })}
              />
              <CreateLocationLogDrawer
                assetId={id}
                open={createLocationLogDrawerOpen}
                onOpenChange={(open) => {
                  if (!open) setCreateLocationLogDrawerOpen(false);
                }}
                callback={() => {
                  queryClient.invalidateQueries({ queryKey: locationLogKeys.all });
                }}
              />
            </div>
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <LocationLogsTable
          data={flattenedData}
          notFoundDisplay={
            isLoading ? null : filters && filters.length > 0 ? (
              <NoLocationLogsFoundFromFiltersDisplay removeColumnFilters={() => setModuleFilters(MODULE.AssetLocationLogs, [])} />
            ) : (
              <NoLocationLogsCreatedDisplay />
            )
          }
          loading={isLoading}
          onSortingChange={(sorting) =>
            updatePageConfig({
              table: { sorting },
            })
          }
          sorting={sorting}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters) => setModuleFilters(MODULE.AssetLocationLogs, filters)}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
        />
        {hasNextLocationLogsPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextLocationLogsPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>
    </div>
  );
}

export default AssetLocationLogsPage;
